import { connect } from 'react-redux';
import { Box, Grid, Paper, Typography } from '@mui/material';
import darkBarcode from '../../assets/images/darkbarcode.png';
import lightBarcode from '../../assets/images/Bar_code_scan.png';

import {
	getPartValidationDetailsForSerialNumber,
	updatePartValidation,
} from '../../redux/actionCreators/partValidation';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import {
	DARK_MODE,
	NO_PART_FAMILY,
	PART_VALIDATION_ERROR,
	PART_VALIDATION_LOADING,
} from '../../constants';
import { useRedirectToNextScreen } from '../../hooks';
import { ScanOrEnterBarcodeForm } from '../../components';
import { receivePart } from '../../redux/actionCreators/receivePart';

const classNames = {
	paper: {
		height: '100%',
		display: 'flex',
	},
	tableContentContainer: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		padding: '50px',
		background: (theme) => theme.palette.background.paper,
		boxShadow: (theme) => theme.palette.mode === 'dark'
			? '0px 8px 40px rgba(0, 0, 0, 0.24), 0px 4px 20px rgba(0, 0, 0, 0.12)'
			: '0px 8px 40px rgba(45, 74, 45, 0.12), 0px 4px 20px rgba(45, 74, 45, 0.08)',
		borderRadius: '16px',
		border: (theme) => theme.palette.mode === 'dark'
			? '1px solid rgba(255, 255, 255, 0.1)'
			: '1px solid rgba(45, 74, 45, 0.1)',
		backdropFilter: 'blur(20px)',
		position: 'relative',
		overflow: 'hidden',
		'&::before': {
			content: '""',
			position: 'absolute',
			top: '0',
			left: '0',
			right: '0',
			bottom: '0',
			background: (theme) => theme.palette.mode === 'dark'
				? 'linear-gradient(135deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0.01) 50%, rgba(255, 255, 255, 0.02) 100%)'
				: 'linear-gradient(135deg, rgba(45, 74, 45, 0.02) 0%, rgba(45, 74, 45, 0.01) 50%, rgba(45, 74, 45, 0.02) 100%)',
			borderRadius: '16px',
			pointerEvents: 'none',
		}
	},
	textInputContainer: {
		width: { sm: '100%', md: '80%' },
	},
};

const mapStateToProps = (state) => {
	return {
		themeMode: state.theme.themeMode,
	};
};

const mapDispatchToProps = (dispatch) => ({
	receivePart: (serialNumber, successCallback, errorCallback) =>
		dispatch(receivePart(serialNumber, successCallback, errorCallback)),
	getPartValidationDetailsForSerialNumber: (
		successCallback,
		errorCallback,
		sequenceNumber,
	) =>
		dispatch(
			getPartValidationDetailsForSerialNumber(
				successCallback,
				errorCallback,
				sequenceNumber,
			),
		),
	updatePartValidation: (data) => dispatch(updatePartValidation(data)),
});

export const PartValidation = ({
	getPartValidationDetailsForSerialNumber,
	receivePart,
	updatePartValidation,
	themeMode,
}) => {
	const { showMessageOverlay } = useMessageContextManager();

	const redirect = useRedirectToNextScreen();

	const validateParentSerialNumber = (serialNumber, resetBarcodeValue) => {
		const updatedScannedValue = serialNumber.substring(0, 17);
		const closeMesssageOverlay = showMessageOverlay({
			contextText: PART_VALIDATION_LOADING.SERIAL_NUMBER_ERROR_CONTEXT_TEXT,
			loading: true,
		});

		receivePart(
			updatedScannedValue,
			(data) => {
				getPartValidationDetailsForSerialNumber(
					(data) => {
						let familyId;
						const currentPart = data.scanJobDetails.find(
							(part) => part.scanJobResults.length < part.quantity,
						);
						if (!currentPart)
							familyId =
								data.scanJobDetails[data.scanJobDetails.length - 1]?.familyId;
						else familyId = currentPart.familyId;

						closeMesssageOverlay();
						resetBarcodeValue();
						redirect(() => null, {
							paramId: familyId ?? NO_PART_FAMILY,
							useSameOperation: true,
						});
					},
					(error) => {
						closeMesssageOverlay();
						resetBarcodeValue();
						updatePartValidation({});
						showMessageOverlay({
							contextText:
								PART_VALIDATION_ERROR.ERROR_CONTEXT_TEXT + 'Part Validation',
							info: error?.response?.data?.message,
							error: true,
						});
						// logError(`Get part validation details ${error}`);
					},
					updatedScannedValue,
				);
			},
			(error) => {
				closeMesssageOverlay();
				resetBarcodeValue();
				updatePartValidation({});
				showMessageOverlay({
					contextText:
						PART_VALIDATION_ERROR.ERROR_CONTEXT_TEXT + 'Part Validation',
					info: error?.response?.data?.message,
					error: true,
				});
				// logError(`Receive part and booking flow ${error}`);
			},
		);
	};
	return (
		<Box
			sx={{
				position: 'relative',
				'&::before': {
					content: '""',
					position: 'absolute',
					top: '-2px',
					left: '-2px',
					right: '-2px',
					bottom: '-2px',
					background: (theme) => theme.palette.mode === 'dark'
						? 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.05))'
						: 'linear-gradient(135deg, rgba(45, 74, 45, 0.1), rgba(45, 74, 45, 0.02))',
					borderRadius: '18px',
					zIndex: -1,
					filter: 'blur(4px)',
				}
			}}
		>
			<Paper sx={classNames.tableContentContainer}>
				<Grid container>
					<Typography
						variant="h4"
						sx={{ 
							color: (theme) => theme.palette.primary.main,
							fontWeight: 800,
							letterSpacing: '1px',
							textShadow: (theme) => theme.palette.mode === 'dark' 
								? '0px 2px 4px rgba(0, 0, 0, 0.2)' 
								: '0px 2px 4px rgba(45, 74, 45, 0.1)',
							fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem', lg: '2.25rem' },
							position: 'relative',
							'&::after': {
								content: '""',
								position: 'absolute',
								bottom: '-4px',
								left: '0',
								right: '0',
								height: '2px',
								background: (theme) => theme.palette.mode === 'dark'
									? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)'
									: 'linear-gradient(90deg, transparent, rgba(45, 74, 45, 0.2), transparent)',
								borderRadius: '1px',
							}
						}}
					>
						Starting Part Validation
					</Typography>
				</Grid>
				<Grid
					container
					direction="column"
					margin="auto"
					columnSpacing={2}
					mt={6}
					sx={{
						flexGrow: 1,
						width: '60%',
						verticalAlign: 'center',
						justifyContent: 'center',
					}}
				>
					<Grid 
						item 
						container 
						alignItems="center" 
						justifyContent="center"
						sx={{ gap: 3, mb: 2 }}
					>
						<Typography 
							variant="h2" 
							sx={{ 
								color: (theme) => theme.palette.primary.main,
								fontWeight: 900,
								letterSpacing: '2px',
								textShadow: (theme) => theme.palette.mode === 'dark'
									? '0px 4px 8px rgba(0, 0, 0, 0.3)'
									: '0px 4px 8px rgba(45, 74, 45, 0.2)',
								textAlign: 'center',
								fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
								position: 'relative',
								'&::before': {
									content: '""',
									position: 'absolute',
									top: '-8px',
									left: '-8px',
									right: '-8px',
									bottom: '-8px',
									background: (theme) => theme.palette.mode === 'dark'
										? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.02))'
										: 'linear-gradient(135deg, rgba(45, 74, 45, 0.05), rgba(45, 74, 45, 0.02))',
									borderRadius: '12px',
									zIndex: -1,
									filter: 'blur(8px)',
								},
								'&::after': {
									content: '""',
									position: 'absolute',
									bottom: '-6px',
									left: '50%',
									transform: 'translateX(-50%)',
									width: '60%',
									height: '3px',
									background: (theme) => theme.palette.mode === 'dark'
										? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)'
										: 'linear-gradient(90deg, transparent, rgba(45, 74, 45, 0.2), transparent)',
									borderRadius: '2px',
								}
							}}
						>
							SCAN SERIAL NO. / VIN
						</Typography>
						<Box
							sx={{
								height: { xs: 40, sm: 50, md: 60, lg: 70 },
								width: { xs: 60, sm: 80, md: 100, lg: 120 },
								background: `url(${themeMode === DARK_MODE ? darkBarcode : lightBarcode}) no-repeat center center / contain`,
								borderRadius: '12px',
								boxShadow: (theme) => theme.palette.mode === 'dark'
									? '0px 8px 20px rgba(0, 0, 0, 0.2), inset 0px 1px 0px rgba(255, 255, 255, 0.1)'
									: '0px 8px 20px rgba(45, 74, 45, 0.1), inset 0px 1px 0px rgba(255, 255, 255, 0.2)',
								border: '2px solid',
								borderImage: (theme) => theme.palette.mode === 'dark'
									? 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05)) 1'
									: 'linear-gradient(135deg, rgba(45, 74, 45, 0.2), rgba(45, 74, 45, 0.1), rgba(45, 74, 45, 0.05)) 1',
								position: 'relative',
								flexShrink: 0,
								'&::before': {
									content: '""',
									position: 'absolute',
									top: '-2px',
									left: '-2px',
									right: '-2px',
									bottom: '-2px',
									background: (theme) => theme.palette.mode === 'dark'
										? 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.05))'
										: 'linear-gradient(135deg, rgba(45, 74, 45, 0.1), rgba(45, 74, 45, 0.02))',
									borderRadius: '14px',
									zIndex: -1,
									filter: 'blur(4px)',
								},
								'&::after': {
									content: '""',
									position: 'absolute',
									top: '0',
									left: '0',
									right: '0',
									bottom: '0',
									background: (theme) => theme.palette.mode === 'dark'
										? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))'
										: 'linear-gradient(135deg, rgba(45, 74, 45, 0.02), rgba(45, 74, 45, 0.01))',
									borderRadius: '12px',
									pointerEvents: 'none',
								}
							}}
						/>
					</Grid>
					<Grid
						item
						container
						justifyContent="center"
						alignItems="center"
						padding={2}
						paddingBottom={22}
					>
						<Box
							sx={{
								...classNames.textInputContainer,
								background: (theme) => theme.palette.background.default,
								padding: '40px',
								borderRadius: '12px',
								boxShadow: (theme) => theme.palette.mode === 'dark'
									? '0px 4px 20px rgba(0, 0, 0, 0.15), inset 0px 1px 0px rgba(255, 255, 255, 0.1)'
									: '0px 4px 20px rgba(45, 74, 45, 0.08), inset 0px 1px 0px rgba(255, 255, 255, 0.2)',
								border: (theme) => theme.palette.mode === 'dark'
									? '1px solid rgba(255, 255, 255, 0.08)'
									: '1px solid rgba(45, 74, 45, 0.08)',
								backdropFilter: 'blur(10px)',
								position: 'relative',
								'&::before': {
									content: '""',
									position: 'absolute',
									top: '-2px',
									left: '-2px',
									right: '-2px',
									bottom: '-2px',
									background: (theme) => theme.palette.mode === 'dark'
										? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.02))'
										: 'linear-gradient(135deg, rgba(45, 74, 45, 0.05), rgba(45, 74, 45, 0.02))',
									borderRadius: '14px',
									zIndex: -1,
									filter: 'blur(4px)',
								},
								'&::after': {
									content: '""',
									position: 'absolute',
									top: '0',
									left: '0',
									right: '0',
									bottom: '0',
									background: (theme) => theme.palette.mode === 'dark'
										? 'linear-gradient(135deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.01))'
										: 'linear-gradient(135deg, rgba(45, 74, 45, 0.02), rgba(45, 74, 45, 0.01))',
									borderRadius: '12px',
									pointerEvents: 'none',
								}
							}}
						>
							<ScanOrEnterBarcodeForm
								enableAutoEnter
								isAlphaNumericValidation={false}
								onSubmit={validateParentSerialNumber}
								placeholder="Part / Serial Number Scan"
							/>
						</Box>
					</Grid>
				</Grid>
			</Paper>
		</Box>
	);
};
export default connect(mapStateToProps, mapDispatchToProps)(PartValidation);
